import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

const card = "rounded-2xl border border-[#765944]/20 bg-white/70 p-5";
export default function ProfessionalQuestionsStaff() {
  const nav = useNavigate();
  const { user, initializing } = useAuth();
  const [pending, setPending] = useState([]);
  const [pendingAnswers, setPendingAnswers] = useState([]);
  const [published, setPublished] = useState([]);
  const [answerText, setAnswerText] = useState({});
  const [busy, setBusy] = useState("");
  const [failure, setFailure] = useState("");
  const authorized = Boolean(user && (user.role === "owner" || (user.professional_map_permissions || []).includes("questions_moderate")));

  const reload = useCallback(async () => {
    if (!authorized) return;
    try {
      const [queue, answerQueue, live] = await Promise.all([
        api.get("/professional-map/questions/staff/pending"),
        api.get("/professional-map/questions/staff/pending-answers"),
        api.get("/professional-map/questions", { params: { limit: 30 } }),
      ]);
      setPending(queue.data || []);
      setPendingAnswers(answerQueue.data || []);
      setPublished(live.data.results || []);
      setFailure("");
    } catch (error) {
      setFailure(formatApiError(error));
    }
  }, [authorized]);

  useEffect(() => { reload(); }, [reload]);

  async function perform(id, endpoint, payload) {
    if (busy) return;
    setBusy(id);
    try {
      await api.post(endpoint, payload);
      toast.success("Изменения сохранены");
      setAnswerText((current) => ({ ...current, [id]: "" }));
      await reload();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setBusy("");
    }
  }
  if (initializing) return <p className="p-8">Проверяем доступ…</p>;
  if (!authorized) return (
    <main className="p-8" role="status">Раздел доступен только администрации Anokina Studio.</main>
  );
  return (
    <div className="min-h-screen bg-[#F7F0E5] text-[#3A2B22]" data-testid="pm-questions-staff">
      <main className="mx-auto max-w-[1000px] px-5 pb-20 pt-7 sm:px-9">
        <button type="button" onClick={() => nav("/professional-map/questions")}
          className="inline-flex min-h-[44px] items-center gap-2 text-sm">
          <ArrowLeft className="h-4 w-4" /> К вопросам
        </button>
        <h1 className="mt-6 font-serif text-3xl">Модерация вопросов</h1>
        {failure && <p className="mt-4 text-sm" role="alert">Не удалось загрузить очередь: {failure}</p>}
        <section className="mt-8" aria-label="Ожидают проверки">
          <h2 className="font-serif text-2xl">Ожидают проверки ({pending.length})</h2>
          <div className="mt-4 space-y-3">
            {!pending.length && <p className="text-sm text-[#806B5C]">Нет вопросов на модерации.</p>}
            {pending.map((item) => (
              <article className={card} key={item.id}>
                <p className="font-serif text-lg">{item.title}</p>
                <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-[#685546]">{item.description}</p>
                {item.tried && <p className="mt-2 text-xs">Что пробовали: {item.tried}</p>}
                <div className="mt-4 flex flex-wrap gap-2">
                  <button type="button" disabled={Boolean(busy)}
                    className="min-h-[42px] rounded-xl border border-[#765944]/30 bg-[#FBF7EF] px-4 text-sm font-medium text-[#594435]"
                    onClick={() => perform(item.id,
                      "/professional-map/questions/staff/" + encodeURIComponent(item.id) + "/review",
                      { decision: "publish" })}>Опубликовать</button>
                  <button type="button" disabled={Boolean(busy)}
                    className="min-h-[42px] rounded-xl border border-[#765944]/25 px-4 text-sm"
                    onClick={() => perform(item.id,
                      "/professional-map/questions/staff/" + encodeURIComponent(item.id) + "/review",
                      { decision: "reject" })}>Отклонить</button>
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="mt-10" aria-label="Ответы, ожидающие проверки">
          <h2 className="font-serif text-2xl">Ответы на проверке ({pendingAnswers.length})</h2>
          <div className="mt-4 space-y-3">
            {!pendingAnswers.length && <p className="text-sm text-[#806B5C]">Нет ответов на проверке.</p>}
            {pendingAnswers.map((item) => (
              <article className={card} key={item.answer_id}>
                <p className="font-serif text-lg">{item.question_title}</p>
                <p className="mt-2 text-xs text-[#806B5C]">{item.author_name}</p>
                <p className="mt-2 whitespace-pre-wrap text-sm">{item.text}</p>
                <div className="mt-4 flex gap-2">
                  {["publish", "reject"].map((decision) => (
                    <button type="button" key={decision} disabled={Boolean(busy)}
                      className="min-h-[42px] rounded-xl border border-[#765944]/25 px-4 text-sm"
                      onClick={() => perform(item.answer_id,
                        "/professional-map/questions/staff/" + encodeURIComponent(item.question_id)
                          + "/answers/" + encodeURIComponent(item.answer_id) + "/review",
                        { decision })}>{decision === "publish" ? "Опубликовать" : "Отклонить"}</button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>
        <section className="mt-10" aria-label="Опубликованные вопросы">
          <h2 className="font-serif text-2xl">Опубликованные вопросы</h2>
          <p className="mt-2 text-xs text-[#806B5C]">
            Показаны последние 30 опубликованных вопросов. Проверяй подозрительные ответы и при необходимости скрывай нарушения.
          </p>
          <div className="mt-4 space-y-4">
            {!published.length && <p className="text-sm text-[#806B5C]">Пока нет опубликованных вопросов.</p>}
            {published.map((item) => (
              <article className={card} key={item.id}>
                <p className="font-serif text-lg">{item.title}</p>
                <p className="mt-2 text-xs text-[#806B5C]">{item.answers?.length || 0} ответов
                  {item.resolved ? " · Решено" : ""}</p>
                {item.answers?.map((answer) => (
                  <div key={answer.id} className="mt-3 rounded-xl border border-[#765944]/15 px-3 py-3">
                    <p className="text-xs text-[#806B5C]">{answer.author_name || "Ответ редакции"}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm">{answer.text}</p>
                    <button type="button" disabled={Boolean(busy)} onClick={() => perform(answer.id,
                      "/professional-map/questions/staff/" + encodeURIComponent(item.id)
                        + "/answers/" + encodeURIComponent(answer.id) + "/review",
                      { decision: "hide" })}
                      className="mt-2 min-h-[34px] rounded-lg border border-[#765944]/25 px-3 text-xs">Скрыть ответ</button>
                  </div>
                ))}
                <label className="mt-4 block text-xs">Редакционный ответ
                  <textarea rows={3} minLength={15} maxLength={5000}
                    className="mt-2 w-full rounded-xl border border-[#765944]/25 bg-white/80 p-3 text-sm"
                    value={answerText[item.id] || ""}
                    onChange={(event) => setAnswerText((current) => ({
                      ...current, [item.id]: event.target.value,
                    }))} />
                </label>
                <div className="mt-3 flex flex-wrap gap-2">
                  <button type="button" disabled={Boolean(busy) || (answerText[item.id] || "").trim().length < 15}
                    className="min-h-[42px] rounded-xl border border-[#765944]/30 bg-[#FBF7EF] px-4 text-sm font-medium text-[#594435] disabled:opacity-50"
                    onClick={() => perform(item.id,
                      "/professional-map/questions/staff/" + encodeURIComponent(item.id) + "/answers",
                      { text: (answerText[item.id] || "").trim() })}>Опубликовать ответ</button>
                  <button type="button" disabled={Boolean(busy) || item.resolved || !item.answers?.length}
                    className="min-h-[42px] rounded-xl border border-[#765944]/25 px-4 text-sm disabled:opacity-40"
                    onClick={() => perform(item.id,
                      "/professional-map/questions/staff/" + encodeURIComponent(item.id) + "/resolve")}>Отметить решённым</button>
                  <button type="button" disabled={Boolean(busy)}
                    className="min-h-[42px] rounded-xl border border-[#765944]/25 px-4 text-sm"
                    onClick={() => perform(item.id,
                      "/professional-map/questions/staff/" + encodeURIComponent(item.id) + "/review",
                      { decision: "hide" })}>Скрыть</button>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
